import React, { useState, memo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Container,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BugReportIcon from '@mui/icons-material/BugReport';
import { LoadFormData, DRIVERS, TERMINALS, DELIVERY_STATUSES } from '../config/types';

import { useAppDispatch, useAppSelector } from '../store';
import { uploadLoad, fetchLoads, deleteLoad, updateLoadStatus, updateLoad } from '../store/slices/dispatchSlice';
import { fetchBrokers } from '../store/slices/brokerSlice';
import { fetchInvoices } from '../store/slices/invoiceSlice';
import { findDeliveredLoads } from '../utils/deliveredLoads';
import { SplitInfo, describeStops, splitLabel } from '../services/dispatchImport/split';
import DispatchImport from '../components/loadBoard/DispatchImport';

interface LoadWithId extends LoadFormData {
  id: string;
  fileUrl?: string;
  /** Set on each drop of a split run (imported from dispatch PDFs) */
  split?: SplitInfo;
}

interface Broker {
  id: string;
  address: string;
  email: string;
  name: string;
  phone: string;
  shortname: string;
}

const initialFormState: LoadFormData = {
  broker: '',
  destination: '',
  orderNumber: '',
  loadNumber: '',
  deliveryDate: '',
  gallons: '',
  pumpRequired: false,
  status: DELIVERY_STATUSES.PENDING,
  terminal: '',
  driver: '',
  currentDate: new Date().toISOString().split('T')[0],
  pdfFile: null,
  pdfFileName: ''
};

interface LoadFormProps {
  onSubmit: (e: React.FormEvent) => void;
  data: LoadFormData;
  onChange: (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => void;
}

const LoadForm = memo(({ onSubmit, data, onChange }: LoadFormProps) => {
  const { items: brokers = [] } = useAppSelector((state) => state.brokers);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      onChange({
        target: {
          name: 'pdfFile',
          value: file
        }
      } as unknown as React.ChangeEvent<HTMLInputElement>);
      onChange({
        target: {
          name: 'pdfFileName',
          value: file.name
        }
      } as unknown as React.ChangeEvent<HTMLInputElement>);
    } else if (file) {
      alert('Please upload only PDF files');
    }
  };

  return (
    <Box component="form" onSubmit={onSubmit}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
          <FormControl fullWidth required>
            <InputLabel>Terminal</InputLabel>
            <Select
              name="terminal"
              value={data.terminal}
              label="Terminal"
              onChange={(e) => onChange(e as unknown as React.ChangeEvent<HTMLInputElement>)}
            >
              {TERMINALS.map((terminal) => (
                <MenuItem key={terminal} value={terminal}>
                  {terminal}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
          <FormControl fullWidth>
            <InputLabel>Broker</InputLabel>
            <Select
              name="broker"
              value={data.broker}
              onChange={(e) => onChange(e as unknown as React.ChangeEvent<HTMLInputElement>)}
            >
              {brokers.map((broker: Broker) => (
                <MenuItem key={broker.id} value={broker.shortname}>
                  {broker.shortname}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
          <FormControl fullWidth required>
            <InputLabel>Driver</InputLabel>
            <Select
              name="driver"
              value={data.driver}
              label="Driver"
              onChange={(e) => onChange(e as unknown as React.ChangeEvent<HTMLInputElement>)}
            >
              {DRIVERS.map((driver) => (
                <MenuItem key={driver} value={driver}>
                  {driver}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
          <TextField
            required
            fullWidth
            name="destination"
            label="Destination"
            value={data.destination}
            onChange={onChange}
          />
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
          <TextField
            fullWidth
            name="orderNumber"
            label="Order Number (Optional)"
            value={data.orderNumber}
            onChange={onChange}
          />
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
          <TextField
            required
            fullWidth
            name="loadNumber"
            label="Load Number"
            value={data.loadNumber}
            onChange={onChange}
          />
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
          <TextField
            required
            fullWidth
            type="date"
            name="deliveryDate"
            label="Delivery Date"
            value={data.deliveryDate}
            onChange={onChange}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
          <TextField
            required
            fullWidth
            name="gallons"
            label="Gallons"
            type="number"
            value={data.gallons}
            onChange={onChange}
          />
        </Box>
        <Box sx={{ flex: '1 1 100%' }}>
          <FormControlLabel
            control={
              <Checkbox
                name="pumpRequired"
                checked={data.pumpRequired}
                onChange={onChange}
              />
            }
            label="Pump Required"
          />
        </Box>
        <Box sx={{ flex: '1 1 100%' }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            sx={{ mr: 2 }}
          >
            Upload PDF
            <input
              type="file"
              hidden
              accept=".pdf"
              onChange={handleFileChange}
            />
          </Button>
          {data.pdfFileName && (
            <Typography variant="body2" color="textSecondary">
              Selected file: {data.pdfFileName}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: '1 1 100%' }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
          >
            Add Load
          </Button>
        </Box>
      </Box>
    </Box>
  );
});

const LoadBoard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loads, loading, error } = useAppSelector((state) => state.dispatch);
  const { items: brokers = [] } = useAppSelector((state) => state.brokers);
  const { user } = useAppSelector((state) => state.auth);
  const [formData, setFormData] = useState<LoadFormData>(initialFormState);
  const [editingLoad, setEditingLoad] = useState<LoadWithId | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLoad, setSelectedLoad] = useState<LoadWithId | null>(null);
  const [loadToDelete, setLoadToDelete] = useState<LoadWithId | null>(null);
  const [notice, setNotice] = useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [statusFilter, setStatusFilter] = useState<typeof DELIVERY_STATUSES[keyof typeof DELIVERY_STATUSES] | 'all'>('Pending');

  useEffect(() => {
    dispatch(fetchBrokers());
  }, [dispatch]);

  // Load the board, then mark Delivered any open load that already has an
  // invoice on Pending Loads (Titan: PO/order # and load #; Avfuel: load #).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const loadAndReconcile = async () => {
      const boardLoads = await dispatch(fetchLoads()).unwrap();
      let invoices;
      try {
        invoices = await dispatch(
          fetchInvoices({ isAdmin: user.accountType === 'admin', dogtag: user.dogtag })
        ).unwrap();
      } catch (invoiceError) {
        console.error('Could not check Pending Loads for delivered loads:', invoiceError);
        return;
      }
      if (cancelled) return;

      const matches = findDeliveredLoads(boardLoads, invoices);
      let updated = 0;
      for (const match of matches) {
        try {
          await dispatch(
            updateLoadStatus({ loadId: match.loadId, status: DELIVERY_STATUSES.DELIVERED })
          ).unwrap();
          updated += 1;
        } catch (updateError) {
          console.error('Could not mark load delivered:', match, updateError);
        }
      }
      if (updated && !cancelled) {
        setNotice({
          message: `Marked ${updated} load${updated === 1 ? '' : 's'} Delivered (found on Pending Loads)`,
          severity: 'info'
        });
      }
    };

    loadAndReconcile().catch(loadError => console.error('Failed to load the board:', loadError));
    return () => {
      cancelled = true;
    };
  }, [dispatch, user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = ['broker', 'destination', 'loadNumber', 'deliveryDate', 'gallons', 'terminal', 'driver'] as const;
    const missingFields = requiredFields.filter(field => !formData[field as keyof LoadFormData]);
    
    if (missingFields.length > 0) {
      console.error('Missing required fields:', missingFields);
      // You can show this error in your UI
      return;
    }

    if (editingLoad) {
      setEditingLoad(null);
    } else {
      try {
        const file = formData.pdfFile || undefined;
        
        // Log the data being sent
        console.log('Submitting load data:', {
          loadData: formData,
          file: file ? { name: file.name, size: file.size, type: file.type } : undefined
        });

        const result = await dispatch(uploadLoad({
          loadData: formData,
          file
        })).unwrap();

        console.log('Load uploaded successfully:', result);
        setFormData(initialFormState);
      } catch (error: any) {
        console.error('Failed to upload load:', error);
        const errorMessage = error.message || 'Failed to upload load. Please try again.';
        // You can show this error message in your UI
      }
    }
  };

  const handleStatusChange = async (loadId: string, newStatus: typeof DELIVERY_STATUSES[keyof typeof DELIVERY_STATUSES]) => {
    const loadToUpdate: LoadWithId | undefined = loads.find((load: LoadWithId) => load.loadNumber === loadId);
    if (loadToUpdate && loadToUpdate.id) {
      // A split is one truck run, so every drop moves together.
      const groupId = loadToUpdate.split?.groupId;
      const targets: LoadWithId[] = groupId
        ? loads.filter((load: LoadWithId) => load.split?.groupId === groupId)
        : [loadToUpdate];
      for (const target of targets) {
        try {
          await dispatch(updateLoadStatus({
            loadId: target.id,
            status: newStatus
          })).unwrap();
        } catch (error) {
          console.error('Failed to update load status:', error);
        }
      }
    }
  };

  const handleEditClick = (load: LoadWithId) => {
    setEditingLoad(load);
    setFormData(load);
  };

  const handleEditSave = async () => {
    if (editingLoad && editingLoad.id) {
      try {
        const file = formData.pdfFile || undefined;
        await dispatch(updateLoad({
          loadId: editingLoad.id,
          loadData: formData,
          currentFileUrl: editingLoad.fileUrl,
          file
        })).unwrap();
      setEditingLoad(null);
      setFormData(initialFormState);
      } catch (error: any) {
        console.error('Failed to update load:', error);
        const errorMessage = error.message || 'Failed to update load. Please try again.';
      }
    }
  };

  const handleEditCancel = () => {
    setEditingLoad(null);
    setFormData(initialFormState);
  };

  const handleDelete = async (loadId: string) => {
    const loadToDelete = loads.find((load: LoadWithId) => load.loadNumber === loadId);
    if (loadToDelete && loadToDelete.id) {
      try {
        await dispatch(deleteLoad({
          loadId: loadToDelete.id,
          fileUrl: loadToDelete.fileUrl
        })).unwrap();
      } catch (error) {
        console.error('Failed to delete load:', error);
      }
    }
  };

  const handleDeleteClick = (load: LoadWithId) => {
    setLoadToDelete(load);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (loadToDelete && loadToDelete.id) {
      try {
        await dispatch(deleteLoad({
          loadId: loadToDelete.id,
          fileUrl: loadToDelete.fileUrl
        })).unwrap();
        setDeleteDialogOpen(false);
        setLoadToDelete(null);
      } catch (error) {
        console.error('Failed to delete load:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setLoadToDelete(null);
  };

  // Filter loads based on status
  const filteredLoads = loads.filter((load: LoadWithId) => {
    if (statusFilter === 'all') {
      return true;
    }
    return load.status === statusFilter;
  });

  const getStatusColor = (status: typeof DELIVERY_STATUSES[keyof typeof DELIVERY_STATUSES]) => {
    switch (status) {
      case DELIVERY_STATUSES.PENDING:
        return '#ffa726';
      case DELIVERY_STATUSES.IN_TRANSIT:
        return '#29b6f6';
      case DELIVERY_STATUSES.DELIVERED:
        return '#66bb6a';
      case DELIVERY_STATUSES.DELAYED:
        return '#ef5350';
      case DELIVERY_STATUSES.CANCELLED:
        return '#78909c';
      default:
        return '#000000';
    }
  };

  const handleEventClick = (eventInfo: { event: { id: string } }) => {
    const load = loads.find((l: LoadWithId) => l.loadNumber === eventInfo.event.id);
    if (load) {
      setSelectedLoad(load);
      setEditDialogOpen(true);
    }
  };

  const handleLoadDetailsClose = () => {
    setEditDialogOpen(false);
    setSelectedLoad(null);
  };

  const getCalendarEvents = () => {
    return loads.map((load: LoadWithId) => ({
      id: load.loadNumber,
      title: `${load.split ? `${splitLabel(load.split)} · ` : ''}${load.destination} - ${load.gallons} gallons`,
      date: load.deliveryDate,
      backgroundColor: getStatusColor(load.status),
      borderColor: getStatusColor(load.status),
    }));
  };

  const handleDebug = () => {
    const debugData: LoadFormData = {
      broker: brokers[0].shortname,
      destination: 'Los Angeles International Airport',
      orderNumber: 'ORD-12345',
      loadNumber: 'LOAD-67890',
      deliveryDate: new Date().toISOString().split('T')[0],
      gallons: '5000',
      pumpRequired: true,
      status: DELIVERY_STATUSES.PENDING,
      terminal: TERMINALS[0],
      driver: DRIVERS[0],
      currentDate: new Date().toISOString().split('T')[0],
      pdfFile: null,
      pdfFileName: 'sample_invoice.pdf'
    };

    setFormData(debugData);
    
    console.log('Debug data set');
  };

  const handlePdfView = (fileUrl: string) => {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ 
      mt: { xs: 2, sm: 3, md: 4 }, 
      mb: { xs: 2, sm: 3, md: 4 },
      px: { xs: 1, sm: 2, md: 3 }
    }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        gap: { xs: 2, sm: 0 },
        mb: 3 
      }}>
        <Typography variant="h4" component="h1">
          Load Board
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<BugReportIcon />}
            onClick={handleDebug}
          >
            Debug
          </Button>
        </Box>
      </Box>

      <DispatchImport
        existingLoads={loads}
        onNotify={(message, severity) => setNotice({ message, severity })}
      />

      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', lg: 'row' },
        flexWrap: 'wrap', 
        gap: { xs: 2, sm: 3 } 
      }}>
        <Box sx={{ 
          flex: { xs: '1 1 100%', lg: '1 1 30%' }, 
          minWidth: { xs: '100%', lg: '300px' },
          order: { xs: 2, lg: 1 }
        }}>
          <Paper sx={{ p: { xs: 1, sm: 2 } }}>
            <Typography variant="h6" gutterBottom>
              {editingLoad ? 'Edit Load' : 'Add New Load'}
            </Typography>
            <LoadForm
              onSubmit={handleSubmit}
              data={formData}
              onChange={handleChange}
            />
            {editingLoad && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleEditSave}
                  fullWidth
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleEditCancel}
                  fullWidth
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Paper>
        </Box>

        <Box sx={{ 
          flex: { xs: '1 1 100%', lg: '1 1 65%' }, 
          minWidth: { xs: '100%', lg: '300px' },
          order: { xs: 1, lg: 2 }
        }}>
          <Paper sx={{ p: { xs: 1, sm: 2 } }}>
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              events={getCalendarEvents()}
              eventClick={handleEventClick}
              height="auto"
            />
          </Paper>
        </Box>

        <Box sx={{ 
          flex: '1 1 100%',
          order: { xs: 3, lg: 3 }
        }}>
          <Paper sx={{ p: { xs: 1, sm: 2 } }}>
            <Typography variant="h6" gutterBottom>
              Load List
            </Typography>
            
            {/* Status Filter */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                <InputLabel id="status-filter-label">Filter by Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={statusFilter}
                  label="Filter by Status"
                  onChange={(e) => setStatusFilter(e.target.value as typeof DELIVERY_STATUSES[keyof typeof DELIVERY_STATUSES] | 'all')}
                  size="small"
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  {Object.values(DELIVERY_STATUSES).map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
            <Box sx={{ 
              width: '100%',
              overflow: 'hidden',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <TableContainer 
                component={Paper}
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  '& .MuiTable-root': {
                    minWidth: { xs: 800, sm: 1000, md: 1200 },
                    width: '100%',
                  },
                  '& .MuiTableCell-root': {
                    padding: { xs: '8px 4px', sm: '12px 8px', md: '16px' },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  },
                  '& .MuiTableCell-head': {
                    fontWeight: 'bold',
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  },
                  '& .MuiTableRow-root:hover': {
                    backgroundColor: 'action.hover',
                  },
                  // Ensure smooth scrolling on mobile
                  WebkitOverflowScrolling: 'touch',
                  // Force scrollbar to always show
                  '&::-webkit-scrollbar': {
                    height: '12px',
                    display: 'block',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'grey.100',
                    borderRadius: '6px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'grey.400',
                    borderRadius: '6px',
                    border: '2px solid grey.100',
                    '&:hover': {
                      backgroundColor: 'grey.500',
                    },
                  },
                  // For Firefox
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'grey.400 grey.100',
                }}
              >
                <Table sx={{ minWidth: { xs: 800, sm: 1000, md: 1200 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Broker</TableCell>
                      <TableCell>Load Number</TableCell>
                      <TableCell>Order Number</TableCell>
                      <TableCell>Terminal</TableCell>
                      <TableCell>Driver</TableCell>
                      <TableCell>Destination</TableCell>
                      <TableCell>Delivery Date</TableCell>
                      <TableCell>Gallons</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>PDF</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                      {filteredLoads.map((load: LoadWithId) => (
                        <TableRow key={load.loadNumber}>
                        <TableCell>{load.broker}</TableCell>
                        <TableCell>
                          {load.loadNumber}
                          {load.split && (
                            <Tooltip
                              title={`${load.split.ways}-way split, ${load.split.totalGallons.toLocaleString('en-US')} gal: ${describeStops(load.split.stops)}`}
                            >
                              <Box
                                component="span"
                                sx={{
                                  ml: 1,
                                  px: 0.75,
                                  py: 0.25,
                                  borderRadius: 1,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  backgroundColor: '#EDE9FE',
                                  color: '#5B21B6'
                                }}
                              >
                                {splitLabel(load.split)}
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>{load.orderNumber}</TableCell>
                        <TableCell>{load.terminal}</TableCell>
                        <TableCell>{load.driver}</TableCell>
                        <TableCell>{load.destination}</TableCell>
                        <TableCell>{load.deliveryDate}</TableCell>
                        <TableCell>{load.gallons}</TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: { xs: 80, sm: 120 } }}>
                            <Select
                              value={load.status}
                              onChange={(e) => handleStatusChange(load.loadNumber, e.target.value as typeof DELIVERY_STATUSES[keyof typeof DELIVERY_STATUSES])}
                              sx={{
                                backgroundColor: getStatusColor(load.status),
                                color: 'white',
                                '& .MuiSelect-icon': {
                                  color: 'white',
                                },
                              }}
                            >
                              {Object.values(DELIVERY_STATUSES).map((status) => (
                                <MenuItem key={status} value={status}>
                                  {status}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                          <TableCell>
                            {load.fileUrl && (
                              <Tooltip title="View PDF">
                                <IconButton
                                  size="small"
                                  onClick={() => handlePdfView(load.fileUrl!)}
                                >
                                  <PictureAsPdfIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        <TableCell>
                          <Box display="flex" gap={{ xs: 0.5, sm: 1 }} justifyContent="center">
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(load)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(load)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            )}
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={editDialogOpen}
        onClose={handleLoadDetailsClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Load Details
          <IconButton
            aria-label="close"
            onClick={handleLoadDetailsClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedLoad && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                <Typography variant="subtitle1">Load Number</Typography>
                <Typography variant="body1">{selectedLoad.loadNumber}</Typography>
              </Box>
              <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                <Typography variant="subtitle1">Destination</Typography>
                <Typography variant="body1">{selectedLoad.destination}</Typography>
              </Box>
              <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                <Typography variant="subtitle1">Delivery Date</Typography>
                <Typography variant="body1">{selectedLoad.deliveryDate}</Typography>
              </Box>
              <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                <Typography variant="subtitle1">Gallons</Typography>
                <Typography variant="body1">{selectedLoad.gallons}</Typography>
              </Box>
              <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                <Typography variant="subtitle1">Status</Typography>
                <Typography variant="body1">{selectedLoad.status}</Typography>
              </Box>
              <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                <Typography variant="subtitle1">Pump Required</Typography>
                <Typography variant="body1">
                  {selectedLoad.pumpRequired ? 'Yes' : 'No'}
                </Typography>
              </Box>
              {selectedLoad.fileUrl && (
                <Box sx={{ flex: '1 1 100%' }}>
                  <Button
                    startIcon={<PictureAsPdfIcon />}
                    variant="outlined"
                    onClick={() => handlePdfView(selectedLoad.fileUrl!)}
                  >
                    View PDF
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLoadDetailsClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            Are you sure you want to delete this load? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setNotice(null)} severity={notice?.severity || 'success'} sx={{ width: '100%' }}>
          {notice?.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LoadBoard; 